/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ForwardRefExoticComponent, JSX, RefAttributes } from "react";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useHover } from "react-aria";
import { type Control, useController } from "react-hook-form";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  numericPercentages,
  numericPixels,
  radius,
  spacing,
  stroke,
} from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { TextProps } from "../../../_base/_core/Text/Text.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";

const hallowElement: HallowElement = "textarea";

const inputStyle =
  (props: { marginTop: number }): Interpolation<ThemeType> =>
  (theme) => ({
    background: "none",
    border: "none",
    boxShadow: "none",
    color: theme.semanticColors.primary,
    marginTop: props.marginTop,
    outline: "none",
    paddingBottom: spacing.ms,
    paddingInline: numericPixels[18],
    resize: "none",
    width: numericPercentages[100],
  });
const inputNoPersistentPlaceholderStyle: Interpolation = {
  marginTop: 0,
  paddingBottom: 0,
};
const labelStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: colors.transparent,
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.ms,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  display: "block",
  position: "relative",
  transition: "border 200ms ease-in",
  width: numericPercentages[100],
});
const labelFocusedStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: theme.semanticColors.primary,
});
const labelHoveredStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: theme.semanticColors.neutralsVeryLow,
});
const placeholderStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  left: numericPixels[18],
  pointerEvents: "none",
  position: "absolute",
  top: numericPixels[22],
  transformOrigin: "0% 50%",
  transition: "all 250ms",
});
const placeholderHiddenStyle: Interpolation = {
  opacity: 0,
};
const populatedStyle: Interpolation = {
  top: numericPixels[10],
  transform: "scale(.75)",
};
const wordCountStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  pointerEvents: "none",
  position: "absolute",
  right: numericPixels[18],
  top: numericPixels[10],
});

export type TextAreaProps = HallowElementProps<typeof hallowElement> & {
  isPersistentPlaceholder?: boolean;
  placeholderCSS?: Interpolation;
  textProps?: Partial<TextProps>;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      isPersistentPlaceholder = true,
      name,
      value,
      placeholder,
      placeholderCSS = {},
      textProps = {},
      ...props
    },
    ref,
  ) => {
    const placeholderRef = useRef<HTMLSpanElement>(null);
    const [placeholderHeight, setPlaceholderHeight] = useState<number>(28);
    const [focused, setFocused] = useState<boolean>(false);
    const { hoverProps, isHovered: hovered } = useHover({});

    useEffect(() => {
      const checkPlaceholder = () => {
        if (placeholderRef.current) {
          setPlaceholderHeight(
            placeholderRef.current.getBoundingClientRect().height + 16, // add sufficient spacing
          );
        }
      };
      if (!!placeholder) {
        checkPlaceholder();
        if (typeof window !== "undefined") {
          window.addEventListener("resize", checkPlaceholder);
        }
      }

      return () => {
        if (typeof window !== "undefined") {
          window.removeEventListener("resize", checkPlaceholder);
        }
      };
    }, [placeholder]);

    return (
      <label
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        css={[
          labelStyle,
          focused && labelFocusedStyle,
          hovered && !focused && labelHoveredStyle,
        ]}
        className={props.className}
        {...hoverProps}
      >
        <Text
          css={[
            placeholderStyle,
            (focused || !!value) && populatedStyle,
            (focused || value) &&
              !isPersistentPlaceholder &&
              placeholderHiddenStyle,
            placeholderCSS,
          ]}
          {...({
            ...{
              size: "m",
              type: "body",
            },
            ...textProps,
          } as TextProps)}
          ref={placeholderRef}
        >
          {placeholder}
        </Text>
        {props.maxLength && (focused || !!value) && (
          <Text size="l" type="detail" css={wordCountStyle}>
            {value?.toString().length}/{props.maxLength}
          </Text>
        )}
        <Text
          asChild
          {...({
            size: "m",
            type: "body",
            ...textProps,
          } as TextProps)}
        >
          <textarea
            css={[
              inputStyle({ marginTop: placeholderHeight }),
              (focused || value) &&
                !isPersistentPlaceholder &&
                inputNoPersistentPlaceholderStyle,
            ]}
            {...props}
            value={value}
            ref={ref}
          />
        </Text>
      </label>
    );
  },
) as ForwardRefExoticComponent<
  Omit<TextAreaProps, "ref"> & RefAttributes<HTMLTextAreaElement>
> & { Field: (props: TextAreaFieldProps) => JSX.Element };

type TextAreaFieldProps = TextAreaProps & {
  control: Control<any, any>;
};

TextArea.Field = ({
  control,
  name,
  required,
  ...props
}: TextAreaFieldProps) => {
  const {
    field: { ref, ...field },
  } = useController({
    name,
    control,
    rules: { required },
  });

  return (
    <TextArea
      {...field}
      {...props}
      value={field.value}
      ref={ref}
      onChange={(event) => field.onChange?.(event?.target.value)}
    />
  );
};

TextArea.displayName = "TextArea";
